import { PublicClientApplication, LogLevel } from '@azure/msal-browser';
import type { BrowserAuthOptions, HandleRedirectPromiseOptions } from '@azure/msal-browser';
import type { Configuration } from '@azure/msal-browser';

const auth: BrowserAuthOptions = {
  clientId: import.meta.env.VITE_ENTRA_CLIENT_ID,
  authority: import.meta.env.VITE_ENTRA_AUTHORITY,
  redirectUri: import.meta.env.VITE_ENTRA_REDIRECT_URI,
  postLogoutRedirectUri: import.meta.env.VITE_ENTRA_POST_LOGOUT_REDIRECT_URI,
};

/**
 * Options for the bootstrap's `handleRedirectPromise()` drain.
 *
 * `navigateToLoginRequestUrl` makes MSAL return the user to the URL sign-in was
 * launched from rather than leaving them on `redirectUri` (`/`). Since
 * DDR-PLT-024 `RequireAuth` initiates login from the deep link itself, so that
 * URL is the route the user actually asked for — restoring it is what makes an
 * emailed or bookmarked deep link survive the round trip through Entra.
 *
 * It is passed explicitly even though `true` is MSAL's default, because it is
 * load-bearing behaviour we now depend on rather than an incidental one. Note
 * it is **not** a `Configuration.auth` field: in `@azure/msal-browser` 5.x the
 * only readers are `RedirectClient.handleRedirectPromise` and
 * `PlatformAuthInteractionClient`, both reading `options?.navigateToLoginRequestUrl`
 * off this argument. Setting it under `auth` (as this file previously did) type-
 * checks against a widened type but is silently inert.
 *
 * Cost: when the login-request URL differs from `redirectUri`, MSAL caches the
 * response hash and performs one extra document load to restore it before
 * processing. Functionally correct, slight flicker.
 *
 * `/sign-in` degrades correctly: an explicit click there returns to `/sign-in`,
 * which forwards an already-authenticated visitor to the index rather than
 * stranding them (see `SignInPage`).
 */
export const redirectPromiseOptions: HandleRedirectPromiseOptions = {
  navigateToLoginRequestUrl: true,
};

/**
 * MSAL configuration for the SPA. Reads all tenant-specific values from
 * `import.meta.env` so the same bundle can be promoted across environments
 * via per-environment `.env.{mode}` files. Real client IDs and tenant IDs are
 * never committed - see `.env.example` for the contract.
 */
export const msalConfig: Configuration = {
  auth,
  cache: {
    cacheLocation: 'sessionStorage',
  },
  system: {
    loggerOptions: {
      logLevel: LogLevel.Warning,
      piiLoggingEnabled: false,
      loggerCallback: (level, message) => {
        if (level === LogLevel.Error) {
          console.error(message);
        }
      },
    },
  },
};

/**
 * Scopes requested when calling the AMI API. Comma-separated in
 * `VITE_API_SCOPES` to allow per-environment overrides without code changes.
 *
 * Evaluated lazily on every call so tests can override the value via
 * `vi.stubEnv` after the module has been imported.
 */
export function getApiScopes(): string[] {
  return (import.meta.env.VITE_API_SCOPES ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/**
 * Singleton MSAL instance. `main.tsx` invokes `initialize()` before rendering
 * so that the axios interceptor can call `acquireTokenSilent` safely.
 */
export const msalInstance = new PublicClientApplication(msalConfig);
